import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import type { Me } from "../lib/types";

/**
 * useAuth(appName) — Standard-Hooks für Login/Logout/Me.
 *
 * `appName` muss zum Backend-Server-`vg_user_app.app_name` passen, sonst
 * gibt der Server 403 zurück. Routet API-Pfade auf `/api/<appName>/auth/...`.
 */

export function useMe(appName: string) {
  return useQuery<Me>({
    queryKey: ["me", appName],
    queryFn: () => apiGet<Me>(`/api/${appName}/auth/me`),
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin(appName: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string; totp_code?: string }) =>
      apiPost<{ ok: true; must_change_password: boolean }>(`/api/${appName}/auth/login`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", appName] }),
  });
}

export function useLogout(appName: string) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => apiPost<{ ok: true }>(`/api/${appName}/auth/logout`),
    onSuccess: () => {
      qc.clear();
      navigate("/login", { replace: true });
    },
  });
}

export function useChangePassword(appName: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { current_password: string; new_password: string }) =>
      apiPost<{ ok: true }>(`/api/${appName}/auth/change-password`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", appName] }),
  });
}
